import { HttpResponse } from 'msw'
import { constraintViolationResponse } from './models'

const MAX_PICKER_PAGE_SIZE = 100

type PickerCursor = { offset: number; search: string; size: number }

type PickerRequestViolation = { violatedField: 'cursor' | 'page-size' }

type CursorPage<TItem> = {
  _links: { first: { href: string }; self: { href: string }; next?: { href: string } }
  _embedded: { items: TItem[] }
}

/** What a picker handler answers: a page, or the request field the platform would reject. */
export type PickerPageAnswer<TPage> = TPage | PickerRequestViolation

const constraintViolation = ({ violatedField }: PickerRequestViolation) =>
  constraintViolationResponse([
    {
      message:
        violatedField === 'cursor'
          ? 'is not a cursor this listing issued'
          : `must be between 1 and ${MAX_PICKER_PAGE_SIZE}`,
      field: violatedField,
    },
  ])

const isPickerCursor = (value: unknown): value is PickerCursor =>
  typeof value === 'object' &&
  value !== null &&
  'offset' in value &&
  Number.isInteger(value.offset) &&
  'search' in value &&
  typeof value.search === 'string' &&
  'size' in value &&
  typeof value.size === 'number'

const readPickerCursor = (cursor: string, { search, size }: Omit<PickerCursor, 'offset'>) => {
  let decoded: unknown
  try {
    decoded = JSON.parse(atob(cursor))
  } catch {
    return undefined
  }
  if (!isPickerCursor(decoded) || decoded.offset < 0 || decoded.search !== search || decoded.size !== size) {
    return undefined
  }
  return decoded.offset
}

/**
 * One page of `matching`, paged the way pixee-platform#6713 pages every picker: by an opaque cursor
 * that a page's `next` link carries, issued for one search and page size. A cursor the listing did
 * not issue for this request is a 400 on `cursor`, as on the platform. A missing or out-of-range
 * `page-size` is a 400 too, which is stricter than the platform's default size: every picker sends
 * one, so a request without it is a client bug a test should see.
 */
export const pageOf = <TItem>(request: Request, matching: TItem[]): PickerPageAnswer<CursorPage<TItem>> => {
  const url = new URL(request.url)
  const size = Number(url.searchParams.get('page-size'))
  if (!Number.isInteger(size) || size < 1 || size > MAX_PICKER_PAGE_SIZE) {
    return { violatedField: 'page-size' }
  }
  const search = url.searchParams.get('search') ?? ''
  // The platform reads an empty `cursor=` as no cursor at all.
  const cursor = url.searchParams.get('cursor') || undefined
  const offset = cursor === undefined ? 0 : readPickerCursor(cursor, { search, size })
  if (offset === undefined) {
    return { violatedField: 'cursor' }
  }
  const firstUrl = new URL(url)
  firstUrl.searchParams.delete('cursor')
  const nextUrl = new URL(url)
  nextUrl.searchParams.set(
    'cursor',
    btoa(JSON.stringify({ offset: offset + size, search, size } satisfies PickerCursor))
  )
  return {
    _links: {
      first: { href: `${firstUrl.pathname}${firstUrl.search}` },
      self: { href: `${url.pathname}${url.search}` },
      next: offset + size < matching.length ? { href: `${nextUrl.pathname}${nextUrl.search}` } : undefined,
    },
    _embedded: { items: matching.slice(offset, offset + size) },
  }
}

/** One page of the items whose `matchedText` contains the request's `search`, ignoring case. */
export const pageOfCaseInsensitiveMatches = <TItem>(
  request: Request,
  items: TItem[],
  matchedText: (item: TItem) => string
) => {
  const search = new URL(request.url).searchParams.get('search')?.toLowerCase() ?? ''
  return pageOf(
    request,
    items.filter(item => matchedText(item).toLowerCase().includes(search))
  )
}

export const respondWithPage = (answer: PickerPageAnswer<CursorPage<unknown>>) =>
  'violatedField' in answer ? constraintViolation(answer) : HttpResponse.json(answer)
